// Every row of a /data-agg job, not the 100-row preview. A job submitted with `full: true` writes its rows to files,
// and GET /data-agg/results/{id}/csv (telcofy-demo#110) streams them as one CSV: UTF-8 with a byte order mark, CRLF,
// RFC 4180 quoting, the header from the job's columns. This is the one reader of full exports: Export all rows
// downloads what it reads, and the dashboard reads the same rows as records. The route answers 400 for a job that
// is not a full export and 409 for an export that has not finished; those stay ApiErrors with the API's own message.
//
// A 404 means the API has no such route yet: that is a FullResultsError, so a page can say plainly that the all-rows
// export isn't available instead of showing a preview as if it were everything. Nothing is logged.
import { DATA_API_BASE_URL } from '../config/api';
import { csvRecords, mergeCsvTables, parseCsv } from '../utils/csv';
import { JobError, LONG_JOB_TIMING, runDataJobWith } from './dataJobs';
import type { RunJobOptions } from './dataJobs';
import { ApiError, getText } from './http';

/** What a page says when the API cannot hand over a full export as CSV. */
export const FULL_EXPORT_UNAVAILABLE = "The all-rows export isn't available yet.";

/** A full export the API has no CSV route for. */
export class FullResultsError extends Error {
  readonly jobId: string;

  constructor(message: string, jobId: string) {
    super(message);
    this.name = 'FullResultsError';
    this.jobId = jobId;
    Object.setPrototypeOf(this, FullResultsError.prototype);
  }
}

const unreadable = (jobId: string) => new JobError('bad-response', 'The API sent a CSV this page cannot read.', jobId);

/** GET /data-agg/results/{id}/csv, parsed: the header row first. Every value is still text. */
export async function readResultCsv(jobId: string, signal?: AbortSignal): Promise<string[][]> {
  let text: string;
  try {
    text = await getText(DATA_API_BASE_URL, `/data-agg/results/${encodeURIComponent(jobId)}/csv`, { signal });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) throw new FullResultsError(FULL_EXPORT_UNAVAILABLE, jobId);
    throw e;
  }
  try {
    return parseCsv(text);
  } catch {
    throw unreadable(jobId);
  }
}

/**
 * Submit `body` as a full export, wait for it and read its CSV. Rejects with an AbortError, a JobError (failed, timed
 * out, a CSV that cannot be read), an ApiError or a FullResultsError.
 */
export function runFullCsvJob(body: object, o: RunJobOptions = {}): Promise<{ jobId: string; table: string[][] }> {
  return runDataJobWith({ ...body, full: true }, { timing: LONG_JOB_TIMING, ...o }, async (jobId, signal) => ({
    jobId,
    table: await readResultCsv(jobId, signal),
  }));
}

/** runFullCsvJob as records keyed by column: an empty cell is null, numbers are still text. */
export async function runFullDataJob(body: object, o: RunJobOptions = {}): Promise<{ jobId: string; rows: Record<string, string | null>[] }> {
  const { jobId, table } = await runFullCsvJob(body, o);
  if (table.length === 0) return { jobId, rows: [] };
  try {
    return { jobId, rows: csvRecords(table) };
  } catch {
    throw unreadable(jobId);
  }
}

/** One job of an all-rows export; `fill` names the columns the job's rows leave out because the request says them. */
export interface ExportPart {
  body: object;
  fill?: Readonly<Record<string, string>>;
}

/** The rows of an all-rows export, one job per part (one per cell size), as one table with one header row. */
export async function exportAllRows(parts: readonly ExportPart[], o: RunJobOptions = {}): Promise<string[][]> {
  const tables = await Promise.all(parts.map((p, i) => runFullCsvJob(p.body, i === 0 ? o : { ...o, onSubmitted: undefined })));
  return mergeCsvTables(tables.map((t, i) => ({ table: t.table, fill: parts[i].fill })));
}
